/**
 * The shipped monster archetypes (PRD #793).
 *
 * The first family is the Ratmen, chosen because all three bodies, hues, and
 * sound families already exist in the bestiary and because they map cleanly onto
 * the three roles: a pursuer, an archer, and a caster. Role work was therefore
 * never blocked on art.
 *
 * The Skeleton Warrior is here too, and it is the regression: it is the monster
 * the dungeon has always spawned, declared as an archetype with the health, the
 * damage, and the walk speed it has always had, so moving the dungeon onto this
 * format changes what a run feels like not at all.
 *
 * Every number in this file is first-pass and owned by the tuning PRD (#799).
 */

import { deriveCombatStats, type CombatStats } from '../combat/derivedStats';
import { monsters } from '../content';
import type { ArpgMonsterArchetype, ArpgMonsterArchetypeId } from './types';

const ARCHETYPES: readonly ArpgMonsterArchetype[] = [
  {
    id: 'skeleton-warrior',
    bestiaryId: 'skeleton-warrior',
    base: { level: 1, maxHealth: 70, mainHandDamage: 12 },
    loadout: ['monster-strike'],
    brain: 'melee-pursuer',
    msPerTile: 320,
    difficultyWeight: 1,
    lootTable: 'melee-drops',
    xpValue: 25,
  },
  {
    id: 'ratman',
    bestiaryId: 'ratman',
    // Lighter and faster than the skeleton: the family's job is to arrive in
    // numbers, and a pack that hits as hard as a skeleton each is not a pack.
    base: { level: 1, maxHealth: 46, mainHandDamage: 8 },
    loadout: ['monster-strike'],
    brain: 'melee-pursuer',
    msPerTile: 280,
    difficultyWeight: 0.8,
    lootTable: 'melee-drops',
    xpValue: 18,
  },
  {
    id: 'ratman-archer',
    bestiaryId: 'ratman-archer',
    // Frailer than its melee cousin, because its position is its defence and
    // taking that position away from it is the answer.
    base: { level: 1, maxHealth: 38, mainHandDamage: 9 },
    loadout: ['monster-arrow'],
    brain: 'ranged-kiter',
    msPerTile: 300,
    difficultyWeight: 1.1,
    lootTable: 'ranged-drops',
    xpValue: 24,
  },
  {
    id: 'ratman-mage',
    bestiaryId: 'ratman-mage',
    // The only archetype that adopts a telegraphed area end to end. Its bolt is
    // deliberately weak: the Geyser is the threat, and the bolt only exists so
    // that ignoring the caster between Geysers is not free.
    base: { level: 1, maxHealth: 40, mainHandDamage: 10 },
    loadout: ['geyser', 'monster-bolt'],
    brain: 'telegraphed-caster',
    msPerTile: 300,
    difficultyWeight: 1.4,
    lootTable: 'caster-drops',
    xpValue: 30,
  },
  {
    // The first boss (PRD #837, slice 5): the Ogre Lord that ends Despise
    // level 1. Its kit is an ordinary caster kit plus exactly one extra
    // telegraphed catalog ability - Meteor Shower, five announced impacts the
    // player answers by leaving the footprints - so the counterplay law binds
    // it like any monster and no kernel, brain, or wire code learns the word
    // "boss". The population plan places it in the boss landmark room wearing
    // the elite promotion, which is where its elite-style multipliers and its
    // guaranteed drop come from; `boss-drops` is what makes that drop a Rare.
    //
    // The id is the string the campaign graph exports as
    // DESPISE_BOSS_ARCHETYPE_ID. It stays a literal here because the union in
    // types.ts is type-level and importing campaign.ts from this module would
    // cycle through the planner; the archetype guard test holds the two equal.
    id: 'despise-boss',
    bestiaryId: 'ogre-lord',
    base: { level: 5, maxHealth: 260, mainHandDamage: 14 },
    loadout: ['meteor-shower', 'monster-bolt'],
    brain: 'telegraphed-caster',
    msPerTile: 340,
    difficultyWeight: 6,
    lootTable: 'boss-drops',
    xpValue: 150,
  },
];

export const arpgMonsterArchetypes: ReadonlyMap<
  ArpgMonsterArchetypeId,
  ArpgMonsterArchetype
> = new Map(ARCHETYPES.map((archetype) => [archetype.id, archetype]));

/** Look up an archetype. An unknown id is null rather than a throw. */
export function arpgMonsterArchetype(
  id: string,
): ArpgMonsterArchetype | null {
  return arpgMonsterArchetypes.get(id as ArpgMonsterArchetypeId) ?? null;
}

/**
 * How much of its declared base an archetype gains per level above its
 * declared base level (PRD #837 slice 7). Linear, monotonic, and shared by
 * every archetype, so a tier is stronger by arithmetic rather than by any
 * branch, and a level below base scales the same line downward (floored well
 * above zero by the one-point minimum in {@link archetypeBaseAtLevel}).
 * First-pass numbers owned by the tuning PRD (#799) like every base beside
 * them.
 */
export const ARPG_MONSTER_LEVEL_HEALTH_GROWTH = 0.12;
export const ARPG_MONSTER_LEVEL_DAMAGE_GROWTH = 0.1;

/**
 * The base stats this archetype fights with at one planned level (PRD #837
 * slice 7): the declared base, scaled linearly by the level difference from
 * the declared base level. The declared level is the identity line: an
 * archetype asked for its own base level answers its base untouched.
 */
export function archetypeBaseAtLevel(
  archetype: ArpgMonsterArchetype,
  level: number,
): { level: number; maxHealth: number; mainHandDamage: number } {
  const bounded = Number.isSafeInteger(level) ? Math.max(1, level) : archetype.base.level;
  const steps = bounded - archetype.base.level;
  return {
    level: bounded,
    maxHealth: Math.max(
      1,
      Math.round(
        archetype.base.maxHealth * (1 + steps * ARPG_MONSTER_LEVEL_HEALTH_GROWTH),
      ),
    ),
    mainHandDamage: Math.max(
      1,
      Math.round(
        archetype.base.mainHandDamage *
          (1 + steps * ARPG_MONSTER_LEVEL_DAMAGE_GROWTH),
      ),
    ),
  };
}

/**
 * The derived record this archetype fights with.
 *
 * The same fold a Hero goes through, with no class curve and no allocation: a
 * monster declares its damage band and its resists, and everything else derives
 * to the baseline. That is what makes it buffable and slowable through the
 * machinery that already exists rather than through a second one.
 *
 * A planned level (PRD #837 slice 7) scales the declared vitals and damage
 * band through {@link archetypeBaseAtLevel} before the fold; absent, the
 * archetype fights at its declared base exactly as before tiers existed.
 */
export function archetypeCombatStats(
  archetype: ArpgMonsterArchetype,
  level?: number,
): CombatStats {
  const base = archetypeBaseAtLevel(archetype, level ?? archetype.base.level);
  return deriveCombatStats({
    level: base.level,
    mainHand: { damage: base.mainHandDamage, grip: 'one-handed' },
    base: {
      maxHealth: base.maxHealth,
      maxMana: archetype.base.maxMana ?? 0,
    },
    ...(archetype.base.resists ? { resists: archetype.base.resists } : {}),
  });
}

/** The bestiary identity this archetype wears: body, hue, name, sounds. */
export function archetypeIdentity(archetype: ArpgMonsterArchetype) {
  return monsters.get(archetype.bestiaryId);
}

export type { ArpgMonsterArchetype, ArpgMonsterArchetypeId, ArpgMonsterBaseStats } from './types';
